import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { Button } from '../../components/ui/Button';
import { PDFExport } from '@progress/kendo-react-pdf';
import LtyStepper from './LtyStepper';
import { publicUrl, prettyUrl } from './publicUrl';

/**
 * QR Loyalty & Ordering — per-table QR codes (table-service ordering).
 *
 * Each table gets its own QR encoding `${origin}/v/{code}?table={i}` — the guest
 * page reads `?table=` and the order carries that number straight to the kitchen,
 * so the customer never types a table number. Owner can preview, download a
 * single table's PNG, or export ALL tables as a print-ready PDF (one page each)
 * via the same Kendo PDFExport pattern used by the billing InvoicePreview.
 *
 * The table COUNT is now a saved setting (`table_count`), not local state. It
 * used to live in this component's useState, so it vanished on reload and
 * nothing else could see it — but the same number is what the order-first guest
 * picker needs when someone arrives from an ITEM QR on a screen and has no table
 * in their URL. One number, both jobs. Pass `count`/`onCountChange` to bind it to
 * the settings form; without them the component still works standalone.
 */
const MAX_TABLES = 200;
const DEFAULT_TABLES = 10;

export default function TableQrGenerator({ code, venueName, count, onCountChange }) {
  const { t } = useTranslation();
  // Controlled by the settings form when the owner passes a setter; otherwise
  // this keeps its own count so the component is never broken by omission.
  const [localCount, setLocalCount] = useState(DEFAULT_TABLES);
  const controlled = typeof onCountChange === 'function';
  const value = controlled ? (count ?? DEFAULT_TABLES) : localCount;
  const setValue = controlled ? onCountChange : setLocalCount;
  const [tables, setTables] = useState(null); // null until Generate; then [1..count]
  const pdfRef = useRef(null);

  // Encoded URL and printed caption come from the SAME helper, so a poster can
  // never advertise a host the QR doesn't point at (the old literal named the
  // marketing site while the QR correctly used the live origin).
  const tableUrl = (i) => publicUrl(`/v/${code}?table=${i}`);
  const tableLabel = (i) => prettyUrl(`/v/${code}?table=${i}`);

  const generate = () => {
    const n = Math.max(1, Math.min(MAX_TABLES, parseInt(value, 10) || 1));
    setTables(Array.from({ length: n }, (_, k) => k + 1));
  };

  const downloadPng = (i) => {
    const cv = document.querySelector(`#tqr-cv-${i} canvas`);
    if (!cv) return;
    const a = document.createElement('a');
    a.href = cv.toDataURL('image/png');
    a.download = `${code}-table-${i}.png`;
    a.click();
  };

  const downloadAllPdf = () => pdfRef.current?.save();

  const pdfFileName = useMemo(() => `${code}-table-qr-codes.pdf`, [code]);

  return (
    <div className="lty-tqr">
      <div className="lty-card-title">{t('loyalty.tableqr.title')}</div>
      <div className="lty-card-sub">{t('loyalty.tableqr.sub')}</div>

      <div className="lty-tqr-controls">
        <label className="lty-field-label" htmlFor="tqr-count">{t('loyalty.tableqr.count')}</label>
        <LtyStepper value={value} min={1} max={MAX_TABLES} onChange={setValue} />
        <Button label={t('loyalty.tableqr.generate')} icon="pi pi-qrcode" onClick={generate} />
        {tables && tables.length > 0 && (
          <Button label={t('loyalty.tableqr.download_all')} icon="pi pi-file-pdf" outlined onClick={downloadAllPdf} />
        )}
      </div>

      {tables && (
        <div className="lty-tqr-grid">
          {tables.map((i) => (
            <div key={i} className="lty-tqr-cell">
              <div id={`tqr-cv-${i}`} className="lty-tqr-qr">
                <QRCodeCanvas value={tableUrl(i)} size={132} level="M" marginSize={2} />
              </div>
              <div className="lty-tqr-label">{t('loyalty.tableqr.table', { n: i })}</div>
              <Button
                label={t('loyalty.tableqr.png')}
                icon="pi pi-download"
                text
                size="small"
                onClick={() => downloadPng(i)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Off-screen, one A4 page per table, exported on demand. Kept in the DOM
          (not display:none) so Kendo drawDOM can render it. */}
      {tables && tables.length > 0 && (
        <div style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden="true">
          <PDFExport ref={pdfRef} paperSize="A4" margin="1.5cm" fileName={pdfFileName} forcePageBreak=".tqr-page">
            {tables.map((i) => (
              <div key={i} className="tqr-page" style={{
                textAlign: 'center', padding: '24px 16px',
                fontFamily: 'system-ui, -apple-system, sans-serif', color: '#2A211B',
              }}>
                <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{venueName}</div>
                <div style={{ fontSize: 13, color: '#6E6154', marginBottom: 20 }}>{t('loyalty.tableqr.scan_to_order')}</div>
                {/* SVG (vector) renders crisply through Kendo drawDOM in the PDF. */}
                <QRCodeSVG value={tableUrl(i)} size={300} level="M" marginSize={2} />
                <div style={{ fontSize: 40, fontWeight: 800, marginTop: 18 }}>{t('loyalty.tableqr.table', { n: i })}</div>
                <div style={{ fontSize: 13, color: '#6E6154', fontFamily: 'monospace', marginTop: 8 }}>{tableLabel(i)}</div>
              </div>
            ))}
          </PDFExport>
        </div>
      )}
    </div>
  );
}
